import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth";
import { Avatar } from "../../../../ui/components/Avatar";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { FeatherHelpCircle, FeatherUpload, FeatherArrowRight, FeatherFileText, FeatherHome } from "@subframe/core";

export const CFODashboardPage: React.FC = () => {
  const { company } = useAuth();
  const navigate = useNavigate();

  // Generate client ID from company name
  const generateClientId = (companyName?: string): string => {
    if (!companyName) return "CLIENT-2024-001";
    const initials = companyName
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase();
    return `${initials}-2024-001`;
  };

  // Handle raise query button
  const handleRaiseQuery = (): void => {
    navigate("/cfo/dashboard/queries");
  };

  // Handle navigate to trial balance upload
  const handleNavigateToTrialBalance = (): void => {
    navigate("/cfo/tb/upload_mapping");
  };

  // Handle navigate to reports
  const handleNavigateToReports = (): void => {
    navigate("/cfo/tb/reports");
  };

  // Handle navigate to upload registers
  const handleNavigateToUploadRegisters = (): void => {
    navigate("/cfo/registers/uploadv1");
  };

  // Handle navigate to client overview
  const handleNavigateToClientOverview = (): void => {
    window.open("/client-overview", "_blank");
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Common Header Section */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                size="large"
                image="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueXxlbnwwfHwwfHx8MA%3D%3D"
              />
              <div className="flex flex-col items-start gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    {company?.companyName || "Acme Corporation"}
                  </span>
                  <Badge variant="warning">High Risk</Badge>
                </div>
                <span className="text-body font-body text-subtext-color">
                  Client ID: {generateClientId(company?.companyName)}
                </span>
              </div>
            </div>
            <Button
              variant="neutral-tertiary"
              icon={<FeatherHelpCircle />}
              onClick={handleRaiseQuery}
            >
              Raise Query
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start bg-default-background overflow-auto">
          <div className="flex w-full h-full flex-col items-start p-8 gap-6">
            <div className="text-heading-1 font-heading-1 text-default-font">
              CFO Dashboard
            </div>
            
            {/* Quick Actions */}
            <div className="flex w-full max-w-4xl flex-col items-start gap-4">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Quick Actions
              </span>
              
              {/* Trial Balance Upload Card */}
              <div 
                className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={handleNavigateToTrialBalance}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-md bg-brand-50">
                    <FeatherUpload className="text-brand-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Trial Balance
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      Upload your trial balance file to the database
                    </span>
                  </div>
                </div>
                <FeatherArrowRight className="text-body font-body text-subtext-color" />
              </div>

              {/* Upload Registers Card - hidden for now */}
              {false && (
              <div 
                className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={handleNavigateToUploadRegisters}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-md bg-brand-50">
                    <FeatherFileText className="text-brand-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Upload Registers
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      Upload registers to database.
                    </span>
                  </div>
                </div>
                <FeatherArrowRight className="text-body font-body text-subtext-color" />
              </div>
              )}

              {/* Reports Card */}
              <div 
                className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={handleNavigateToReports}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-md bg-brand-50">
                    <FeatherFileText className="text-brand-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Reports
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      View and manage trial balance reports
                    </span>
                  </div>
                </div>
                <FeatherArrowRight className="text-body font-body text-subtext-color" />
              </div>

              {/* Client Overview Card */}
              <div 
                className="flex w-full items-center justify-between rounded-md border border-solid border-neutral-border bg-default-background p-6 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={handleNavigateToClientOverview}
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-md bg-brand-50">
                    <FeatherHome className="text-brand-600" />
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Client Overview
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      View client dashboard and overview
                    </span>
                  </div>
                </div>
                <FeatherArrowRight className="text-body font-body text-subtext-color" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};

